/** The twelve hazard types, and every per-type constant the rest of the system reads. */

export const HAZARD_TYPES = [
  "aksident",
  "mbyllje_rruge",
  "grope",
  "rreshqitje_dheu",
  "kafshe_ne_rruge",
  "permbytje",
  "punime_pa_ndricim",
  "tombino_e_hapur",
  "semafor_i_prishur",
  "mjet_i_ndalur",
  "objekt_ne_rruge",
  "kontroll_trafiku",
  // Static infrastructure from OpenStreetMap. Not reportable, never spoken:
  // a permanent, quiet layer that makes the map true at zero users.
  "kalim_hekurudhor",
  "rruge_pa_ndricim",
  "kthese_e_forte",
] as const;

/** Types a driver can file. The two OSM layers are facts, not incidents. */
export const REPORTABLE_TYPES = HAZARD_TYPES.filter(
  (t) => t !== "kalim_hekurudhor" && t !== "rruge_pa_ndricim" && t !== "kthese_e_forte",
);

export type HazardType = (typeof HAZARD_TYPES)[number];

export type Severity = "info" | "kujdes" | "rrezik";

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

export type HazardSpec = {
  /** Albanian label shown to the user. The only string a driver ever reads for this type. */
  label: string;
  severity: Severity;
  /** Starting lifetime. */
  baseTtlMs: number;
  /** Each independent confirmation adds this; each rejection subtracts it. */
  bonusMs: number;
  /** Hard ceiling. Confirmations can never push expiry past this. */
  maxTtlMs: number;
  /** Long-lived infrastructure defects: they decay by confidence, not by clock. */
  persistent: boolean;
  /** Two reports of this type inside this radius are the same hazard. */
  dedupRadiusM: number;
  /** Only relevant to traffic travelling the same direction. */
  directional: boolean;
  /** v0 attaches no photos to anything. For kontroll_trafiku this is a legal invariant,
   *  enforced at the API and covered by a test — never relax it. */
  allowsPhoto: false;
};

export const HAZARDS: Record<HazardType, HazardSpec> = {
  aksident: {
    label: "Aksident", severity: "rrezik",
    baseTtlMs: 45 * MIN, bonusMs: 15 * MIN, maxTtlMs: 90 * MIN,
    persistent: false, dedupRadiusM: 400, directional: false, allowsPhoto: false,
  },
  mbyllje_rruge: {
    label: "Mbyllje rruge", severity: "rrezik",
    baseTtlMs: 4 * HOUR, bonusMs: HOUR, maxTtlMs: 6 * HOUR,
    persistent: false, dedupRadiusM: 1000, directional: false, allowsPhoto: false,
  },
  grope: {
    label: "Gropë", severity: "kujdes",
    baseTtlMs: 30 * DAY, bonusMs: 0, maxTtlMs: 30 * DAY,
    persistent: true, dedupRadiusM: 150, directional: false, allowsPhoto: false,
  },
  rreshqitje_dheu: {
    label: "Rrëshqitje dheu", severity: "rrezik",
    baseTtlMs: 12 * HOUR, bonusMs: 6 * HOUR, maxTtlMs: 24 * HOUR,
    persistent: true, dedupRadiusM: 600, directional: false, allowsPhoto: false,
  },
  kafshe_ne_rruge: {
    label: "Kafshë në rrugë", severity: "rrezik",
    baseTtlMs: 30 * MIN, bonusMs: 10 * MIN, maxTtlMs: 45 * MIN,
    persistent: false, dedupRadiusM: 400, directional: false, allowsPhoto: false,
  },
  permbytje: {
    label: "Përmbytje", severity: "rrezik",
    baseTtlMs: 6 * HOUR, bonusMs: 3 * HOUR, maxTtlMs: 12 * HOUR,
    persistent: false, dedupRadiusM: 1000, directional: false, allowsPhoto: false,
  },
  punime_pa_ndricim: {
    label: "Punime pa ndriçim", severity: "kujdes",
    baseTtlMs: 14 * DAY, bonusMs: 0, maxTtlMs: 14 * DAY,
    persistent: true, dedupRadiusM: 250, directional: false, allowsPhoto: false,
  },
  tombino_e_hapur: {
    label: "Tombino e hapur", severity: "rrezik",
    baseTtlMs: 14 * DAY, bonusMs: 0, maxTtlMs: 14 * DAY,
    persistent: true, dedupRadiusM: 100, directional: false, allowsPhoto: false,
  },
  semafor_i_prishur: {
    label: "Semafor i prishur", severity: "kujdes",
    baseTtlMs: 6 * HOUR, bonusMs: 3 * HOUR, maxTtlMs: 12 * HOUR,
    persistent: true, dedupRadiusM: 100, directional: false, allowsPhoto: false,
  },
  mjet_i_ndalur: {
    label: "Mjet i ndalur", severity: "kujdes",
    baseTtlMs: 20 * MIN, bonusMs: 10 * MIN, maxTtlMs: 40 * MIN,
    persistent: false, dedupRadiusM: 250, directional: true, allowsPhoto: false,
  },
  objekt_ne_rruge: {
    label: "Objekt në rrugë", severity: "kujdes",
    baseTtlMs: 30 * MIN, bonusMs: 15 * MIN, maxTtlMs: 60 * MIN,
    persistent: false, dedupRadiusM: 200, directional: false, allowsPhoto: false,
  },
  // Presence only. No photos, no plates, no counts, no unit descriptions, no avoid
  // affordance, no filter that isolates or hides it. Rendered by the identical code
  // path as the other eleven. The 45-minute cap is absolute; see plan section 5.
  kontroll_trafiku: {
    label: "Kontroll trafiku", severity: "kujdes",
    baseTtlMs: 30 * MIN, bonusMs: 10 * MIN, maxTtlMs: 45 * MIN,
    persistent: false, dedupRadiusM: 400, directional: true, allowsPhoto: false,
  },
  kalim_hekurudhor: {
    label: "Kalim hekurudhor", severity: "info",
    baseTtlMs: 3650 * DAY, bonusMs: 0, maxTtlMs: 3650 * DAY,
    persistent: true, dedupRadiusM: 80, directional: false, allowsPhoto: false,
  },
  rruge_pa_ndricim: {
    label: "Rrugë pa ndriçim", severity: "info",
    baseTtlMs: 3650 * DAY, bonusMs: 0, maxTtlMs: 3650 * DAY,
    persistent: true, dedupRadiusM: 400, directional: false, allowsPhoto: false,
  },
  // Computed from road geometry (scripts/seed-curves.mjs): a bend tight enough
  // that a car at the road's speed must brake for it. Visual only, like the other
  // static types: a mountain road would otherwise talk at every hairpin.
  kthese_e_forte: {
    label: "Kthesë e fortë", severity: "info",
    baseTtlMs: 3650 * DAY, bonusMs: 0, maxTtlMs: 3650 * DAY,
    persistent: true, dedupRadiusM: 120, directional: false, allowsPhoto: false,
  },
};

export const KONTROLL_TRAFIKU_MAX_TTL_MS = 45 * MIN;

/** Lifetime given the votes so far. Never exceeds the type's hard ceiling. */
export function ttlFor(type: HazardType, confirms: number, rejections: number): number {
  const spec = HAZARDS[type];
  const raw = spec.baseTtlMs + confirms * spec.bonusMs - rejections * spec.bonusMs;
  return Math.min(spec.maxTtlMs, Math.max(MIN, raw));
}

export function expiresAtFor(
  type: HazardType, createdAtMs: number, confirms: number, rejections: number,
): number {
  return createdAtMs + ttlFor(type, confirms, rejections);
}

/** Laplace-smoothed confidence. Drives whether persistent defects stay on the layer. */
export function confidenceFor(upWeight: number, downWeight: number): number {
  return (2 * upWeight + 1) / (2 * upWeight + downWeight + 2);
}

/* ---------- radial report menu ---------- */

/** 12 o'clock is always grope and 6 o'clock is always aksident in every profile:
 *  muscle memory beats context. Only slot 8 ever swings (see pickProfile callers). */
export type RadialProfile = "interurban" | "urban" | "nate";

export const RADIAL: Record<RadialProfile, readonly HazardType[]> = {
  //         12 o'clock        2                4                  6           8                   10
  interurban: ["grope", "mjet_i_ndalur", "kafshe_ne_rruge", "aksident", "objekt_ne_rruge", "kontroll_trafiku"],
  urban:      ["grope", "mbyllje_rruge", "punime_pa_ndricim", "aksident", "tombino_e_hapur", "kontroll_trafiku"],
  nate:       ["grope", "permbytje", "kafshe_ne_rruge", "aksident", "rreshqitje_dheu", "punime_pa_ndricim"],
};

export function pickProfile(opts: {
  speedMps: number; hourLocal: number; badWeather?: boolean;
}): RadialProfile {
  if (opts.badWeather === true || opts.hourLocal >= 20 || opts.hourLocal < 6) return "nate";
  return opts.speedMps > 19.4 ? "interurban" : "urban"; // 19.4 m/s ~ 70 km/h
}
